'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { BackupScheduleForm, type BackupScheduleFormRef } from '@/components/partials/backup-schedules'
import { ContentError } from '@/components/partials'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import { useBackupSchedule, useUpdateBackupSchedule } from '@/hooks'
import { type UpdateBackupScheduleParams } from '@/types/backup'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const BackupScheduleEditSkeleton = () => (
  <div className="space-y-6">
    {/* Header */}
    <div className="flex items-center justify-between">
      <Skeleton className="h-8 w-52" />
      <div className="flex items-center gap-2">
        <Skeleton className="h-9 w-20" />
        <Skeleton className="h-9 w-16" />
      </div>
    </div>

    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-36" />
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Days checkboxes */}
        <div className="space-y-3">
          <Skeleton className="h-4 w-12" />
          <div className="flex flex-wrap gap-3">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* Time input */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-9 w-40" />
        </div>

        {/* Keep count */}
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-9 w-32" />
        </div>

        {/* DB + Storage checkboxes side by side */}
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="h-4 w-28" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>

        {/* Enabled switch */}
        <div className="flex items-center gap-3">
          <Skeleton className="h-6 w-11 rounded-full" />
          <Skeleton className="h-4 w-16" />
        </div>
      </CardContent>
    </Card>
  </div>
)

const BackupScheduleUpdate = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const formRef = useRef<BackupScheduleFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: backup, isLoading, error } = useBackupSchedule(id)
  const { mutate: updateBackupSchedule, isPending } = useUpdateBackupSchedule()

  const handleSubmit = (data: UpdateBackupScheduleParams) => {
    updateBackupSchedule(
      { id, params: data },
      {
        onSuccess: async updatedBackup => {
          setIsRedirecting(true)
          queryClient.setQueryData(['backup-schedule', id], updatedBackup)
          await queryClient.invalidateQueries({ queryKey: ['backup-schedules'] })
          await queryClient.invalidateQueries({ queryKey: ['backup-schedule', id] })
          toast.success(t('updated_successfully'))
          router.push(`/backups/schedules/${updatedBackup.id}`)
        },
        onError: (error: any) => {
          if (error.response?.status === 422 || error.isValidationError) {
            formRef.current?.handleValidationErrors(error)
            return
          }

          toast.error(t('error_updating'))
        }
      }
    )
  }

  if (isLoading) {
    return <BackupScheduleEditSkeleton />
  }

  if (error || !backup) {
    return <ContentError error={error} listUrl="/backups/schedules" />
  }

  return (
    <BackupScheduleForm
      ref={formRef}
      mode={FormModeEnum.UPDATE}
      initialData={backup}
      onSubmit={handleSubmit}
      onCancel={() => router.push(`/backups/schedules/${id}`)}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default BackupScheduleUpdate
